import { Card } from "components/Card";
import { DropCardPlace } from "constants/cards";
import { DESKTOP_SIDEBAR_WIDTH, Displays } from "constants/layout";
import { CARD_MOVE_TIME } from "constants/move";
import { MonoBehavior } from "game/core/monoBehavior";
import { EventName } from "game/events/eventsName";
import { GameEvent } from "game/events/gameEvent";
import { Layout } from "game/LayoutManager";
import { SolitaireGame } from "game/SolitaireGame";
import { Store } from "game/store/gameStore";
import { Point } from "pixi.js";
import gsap, { Power1 } from 'gsap';

export interface StrategyOptions {
    isShowDifficultyUI?: boolean;
    isShowWastePilePlaceholder?: boolean;
}

export abstract class BaseGameView extends MonoBehavior {

    public static instance: BaseGameView;

    constructor() {
        super();
        BaseGameView.instance = this;
    }

    public abstract draw(cards: string[], onComplete: () => void): void;
    public abstract reDraw(): void;
    
    public abstract getTableSize(): number;
    public abstract getWastePalePosition(cardGap: number): Point;
    public abstract getFoundationPosition(): Point;
    public abstract getAvailableClickPlace(): DropCardPlace[];
}

export abstract class BaseStrategy {

    private settingsTweens: gsap.core.Tween[];

    public static instance: BaseStrategy;

    public get IsShowDifficultyUI() {
        if (this.options.isShowDifficultyUI === undefined) {
            return true;
        }

        return this.options.isShowDifficultyUI;
    }

    public get IsShowWastePilePlaceholder() {
        if (this.options.isShowWastePilePlaceholder === undefined) {
            return true;
        }

        return this.options.isShowWastePilePlaceholder;
    }

    protected view?: BaseGameView;
    protected game!: SolitaireGame;

    constructor(
        protected options: StrategyOptions,
    ) {
        BaseStrategy.instance = this;
        this.view = undefined;

        this.settingsTweens = [];
        GameEvent.add(EventName.TOGGLE_CARD_HIGHLIGHTING, this.handleChangeHighlight.bind(this));
        GameEvent.add(EventName.OPEN_SETTINGS, this.handleOpenSettings.bind(this));
        GameEvent.add(EventName.HIDE_SETTINGS, this.handleHideSettings.bind(this));
    }

    init(game: SolitaireGame, view?: BaseGameView) {
        this.game = game;
        this.view = view;
    }

    public draw(cards: string[], onComplete: () => void) {
        this.view?.draw(cards, onComplete);
    }

    public abstract noHintHandler(): void;

    protected abstract forEachCard(action: (card: Card) => void): void;

    public abstract canDropCardsOnFirstAction(lastCard: Card | null): boolean;

    public abstract handleRemoveCarsFromTable(targetCard: Card | null, cards: Card[]): void;

    public abstract checkWinCondition(): boolean;

    public abstract handleDropCards(moveGroup: Card[], autoPlay: boolean, isClick: boolean): void;

    public abstract handleCheckMoveGroupForPair(moveGroup: Card[], isClick: boolean, isAutoPlay: boolean): void;

    public abstract refreshBoardState(): void;

    public abstract handleInsertCardInToTable(cards: Card[], card: Card): void;

    public abstract handleCardClick(moveGroup: Card[], isClick: boolean): void;

    public getTableSize(): number {
        return this.view?.getTableSize() || 0;
    }

    public getWastePalePosition(cardGap: number): Point {
        return this.view?.getWastePalePosition(cardGap) || new Point();
    }

    public getFoundationPosition(): Point {
        return this.view?.getFoundationPosition() || new Point();
    }

    public getAvailableClickPlace(): DropCardPlace[] {
        return this.view?.getAvailableClickPlace() || [];
    }

    protected handleOpenSettings() {
        const display = Layout.getDisplay();
        if (display !== Displays.DESKTOP) {
            return;
        }

        this.moveCards(DESKTOP_SIDEBAR_WIDTH + 20);
    }


    protected handleHideSettings() {
        const display = Layout.getDisplay();
        if (display !== Displays.DESKTOP) {
            return;
        }

        this.moveCards(0);
    }

    protected moveCards(posX: number) {
        this.settingsTweens = this.settingsTweens.flatMap(tween => {
            tween?.kill();
            return [];
        })

        this.forEachCard((card) => {
            this.settingsTweens.push(gsap.to(card.container.position, { 
                x: posX === 0 ? card.place.position.x : card.container.position.x - posX, 
                ease: Power1.easeOut, 
                duration: CARD_MOVE_TIME 
            }));
        });
    }

    public handleChangeHighlight() {
        this.forEachCard((card) => {
            if (Store.hasHighlighting() && Store.cardHighlighting) {
                card.active ? card.cardDisplay.hideOverlay() : card.cardDisplay.showInactiveOverlay(true);
            } else {
                card.cardDisplay.hideOverlay();
            }
        });
    }

    public onDragCard(moveGroup: Card[]): boolean {
        return false;
    }

    public onCreateMoveGroup(currentMoveGroup: Card[], card: Card) {}

    resize(): void {
        this.reDraw();
    }

    public reDraw(): void {
        this.view?.reDraw();
    }
}